import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Code, Steps, Step, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is OpenPlace?', level: 2 as const },
  { id: 'publish', title: 'Publishing a plate', level: 2 as const },
  { id: 'requirements', title: 'Requirements', level: 2 as const },
  { id: 'url', title: 'Accessing the plate', level: 2 as const },
  { id: 'params', title: 'Passing parameters', level: 2 as const },
]

export function OpenPlace() {
  return (
    <Article toc={toc}>
      <H1>OpenPlace (Public Plates)</H1>
      <Lead>OpenPlace makes a deployed Plate accessible to anyone — no Rabbit account required. It is ideal for customer-facing forms, public dashboards, or kiosk displays.</Lead>

      <H2 id="what">What is OpenPlace?</H2>
      <P>An OpenPlace plate is served at <Code>/open/:plateId</Code> without requiring authentication. The plate renders with a minimal layout (no sidebar, no top bar) — just the sections and views you configured.</P>
      <Callout type="warning">OpenPlace plates expose data to anonymous users. Only publish plates that you intend to be public. Review all views for sensitive data before publishing.</Callout>

      <H2 id="publish">Publishing a plate</H2>
      <Steps>
        <Step n={1} title="Ensure the plate is deployed">A plate must be in <Code>deployed</Code> status before it can be published to OpenPlace.</Step>
        <Step n={2} title="Ensure all required params have fixed values">OpenPlace cannot prompt users for params. Any required param without a fixed value blocks publishing.</Step>
        <Step n={3} title="Click Publish to OpenPlace">On the plate card in <strong>Brainer › Plates</strong>, click the globe icon or the <strong>Publish</strong> button.</Step>
        <Step n={4} title="Share the URL">Copy the <Code>/open/:plateId</Code> URL and share it. Anyone with the link can view the plate.</Step>
      </Steps>
      <Screenshot alt="The Plates list with an OpenPlace badge on one card and a globe icon in the toolbar" caption="Published plates are marked with an OpenPlace badge. Click the globe icon to copy the URL." />

      <H2 id="requirements">Requirements</H2>
      <UL>
        <LI>The plate must be <Code>deployed</Code></LI>
        <LI>All <Code>required</Code> input parameters must have a <Code>fixedValue</Code> — or be supplied via URL query params</LI>
        <LI>Views that use Drinkers must use Drinkers that don't require user identity (no <Code>context.userId</Code> checks)</LI>
      </UL>

      <H2 id="url">Accessing the plate</H2>
      <P>The canonical URL is:</P>
      <pre className="bg-[#1e2433] text-slate-200 p-4 rounded-lg text-[13px] font-mono mb-5">
        {`http://localhost:3004/open/:plateId`}
      </pre>
      <P>Where <Code>:plateId</Code> is the UUID of the plate (shown in the plate detail page URL).</P>

      <H2 id="params">Passing parameters via URL</H2>
      <P>Parameters without a fixed value can be supplied as URL query params:</P>
      <pre className="bg-[#1e2433] text-slate-200 p-4 rounded-lg text-[13px] font-mono mb-5">
        {`http://localhost:3004/open/:plateId?customerId=abc-123&region=EMEA`}
      </pre>
      <P>This is the mechanism for building parameterised public pages — for example, a customer-specific order summary page where the customer ID is embedded in the link sent via email.</P>
      <Callout type="tip">Combine OpenPlace with BUZZ subscriptions: when an email arrives, emit a personalised link to the OpenPlace plate with the recipient's ID in the query string.</Callout>
    </Article>
  )
}
